import React, { useCallback, useEffect, useState } from "react";
import { Wallet, Plus, Edit3, Ban, Link2, Lock, FileClock, ShieldCheck } from "lucide-react";
import { portalApi } from "../../lib/portalApi";

export function fmtAEDFils(fils) {
  if (fils === null || fils === undefined) return "—";
  const v = Number(fils) / 100;
  if (!isFinite(v)) return "—";
  return `AED ${v.toLocaleString("en-AE", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

const CATEGORY_STYLE = {
  payment_received: "bg-[#3F6E2C] text-white",
  material_advance: "bg-[#A6822F] text-white",
  variation_price_revision: "bg-[#6E1F2C] text-white",
  refund_adjustment: "bg-[#3E2A22] text-white",
  other: "bg-[#7A6B5F] text-white",
};

const REV_STATUS_STYLE = {
  draft: "bg-[#7A6B5F] text-white",
  confirmed: "bg-[#3F6E2C] text-white",
  superseded: "bg-[#D8CBBE] text-[#3E2A22]",
};

// Normalizes browser date-input fallbacks (text inputs on browsers without a
// native picker): accepts YYYY-MM-DD, DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY.
export function normalizeDateInput(v) {
  const s = (v || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return null;
}

const inputCls = "w-full border border-[#6E1F2C]/25 bg-white px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]";
const labelCls = "block text-[9px] tracking-[0.22em] uppercase text-[#7A6B5F] mb-1";

function SnapshotCard({ label, value, sub, testid }) {
  return (
    <div className="bg-white border border-[#6E1F2C]/15 p-4" data-testid={testid}>
      <p className="text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F] mb-1.5">{label}</p>
      <p className="text-lg font-semibold text-[#1C110C]">{value}</p>
      {sub && <p className="text-[10px] text-[#7A6B5F] mt-1">{sub}</p>}
    </div>
  );
}

function ReferenceOnlyNotice() {
  return (
    <div className="flex items-center gap-2 bg-[#FAF5EC] border border-[#A6822F]/30 px-3 py-2" data-testid="ledger-reference-only-notice">
      <Lock size={12} className="text-[#A6822F] shrink-0" />
      <span className="text-[10px] text-[#3E2A22]">
        <strong>Reference only</strong> — does not affect milestone schedule or payment triggers.
      </span>
      <span className="ml-auto inline-flex items-center">
        <span className="w-7 h-4 bg-[#A6822F] rounded-full relative opacity-80 cursor-not-allowed" aria-hidden="true">
          <span className="absolute right-0.5 top-0.5 w-3 h-3 bg-white rounded-full" />
        </span>
      </span>
    </div>
  );
}

function ReceiptForm({ slug, categories, milestoneOptions, initial, onDone, onCancel }) {
  const [form, setForm] = useState({
    amount: initial ? String(initial.amount_fils / 100) : "",
    received_date: initial?.received_date || "",
    category: initial?.category || "payment_received",
    description: initial?.description || "",
    milestone_id: initial?.milestone_ref?.id || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    // Read the date straight from the DOM as a fallback for browsers whose
    // native pickers do not fire React onChange, then normalize to ISO.
    const dateEl = e.target.querySelector('[data-testid="ledger-form-date"]');
    const received_date = normalizeDateInput((dateEl && dateEl.value) || form.received_date || "");
    if (!received_date) {
      setErr("Please enter the received date in YYYY-MM-DD format.");
      setBusy(false);
      return;
    }
    try {
      if (initial) {
        await portalApi.updateFinanceReceipt(slug, initial.id, { ...form, received_date });
      } else {
        await portalApi.addFinanceReceipt(slug, { ...form, received_date });
      }
      onDone();
    } catch (ex) { setErr(ex.message || "Failed to save"); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#6E1F2C]/20 p-4 space-y-3" data-testid="ledger-receipt-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Amount (AED)</label>
          <input type="number" step="0.01" min="0.01" required value={form.amount} onChange={set("amount")} className={inputCls} data-testid="ledger-form-amount" />
        </div>
        <div>
          <label className={labelCls}>Received date</label>
          <input type="date" required value={form.received_date} onChange={set("received_date")} className={inputCls} data-testid="ledger-form-date" />
        </div>
        <div>
          <label className={labelCls}>Category</label>
          <select value={form.category} onChange={set("category")} className={inputCls} data-testid="ledger-form-category">
            {Object.entries(categories || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls}>Brief description</label>
        <input type="text" value={form.description} onChange={set("description")} className={inputCls} placeholder="e.g. Payment received" data-testid="ledger-form-description" />
      </div>
      <div>
        <label className={labelCls}>Milestone reference (optional)</label>
        <select value={form.milestone_id} onChange={set("milestone_id")} className={inputCls} data-testid="ledger-form-milestone">
          <option value="">No milestone reference</option>
          {(milestoneOptions || []).map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
      </div>
      <ReferenceOnlyNotice />
      {err && <p className="text-[11px] text-[#A3141A]" data-testid="ledger-form-error">{err}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} data-testid="ledger-form-submit"
          className="px-4 py-2 bg-[#6E1F2C] text-white text-[10px] tracking-[0.22em] uppercase disabled:opacity-60">
          {busy ? "Saving…" : initial ? "Save changes" : "Add receipt"}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-[#6E1F2C]/25 text-[10px] tracking-[0.22em] uppercase text-[#7A6B5F]">
          Cancel
        </button>
      </div>
    </form>
  );
}

function RevisionForm({ slug, initial, onDone, onCancel }) {
  const [form, setForm] = useState({
    revised_value: initial ? String(initial.revised_value_fils / 100) : "",
    effective_date: initial?.effective_date || "",
    reason: initial?.reason || "",
    reference: initial?.reference || "",
    status: initial?.status || "draft",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const dateEl = e.target.querySelector('[data-testid="revision-form-date"]');
    const effective_date = normalizeDateInput((dateEl && dateEl.value) || form.effective_date || "");
    if (!effective_date) {
      setErr("Please enter the effective date in YYYY-MM-DD format.");
      setBusy(false);
      return;
    }
    try {
      if (initial) await portalApi.updateValueRevision(slug, initial.id, { ...form, effective_date });
      else await portalApi.addValueRevision(slug, { ...form, effective_date });
      onDone();
    } catch (ex) { setErr(ex.message || "Failed to save"); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="bg-white border border-[#6E1F2C]/20 p-4 space-y-3" data-testid="ledger-revision-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Revised project value (AED)</label>
          <input type="number" step="0.01" min="0.01" required value={form.revised_value} onChange={set("revised_value")} className={inputCls} data-testid="revision-form-value" />
        </div>
        <div>
          <label className={labelCls}>Effective date</label>
          <input type="date" required value={form.effective_date} onChange={set("effective_date")} className={inputCls} data-testid="revision-form-date" />
        </div>
        <div>
          <label className={labelCls}>Status</label>
          <select value={form.status} onChange={set("status")} className={inputCls} data-testid="revision-form-status">
            <option value="draft">Draft</option>
            <option value="confirmed">Confirmed</option>
            {initial && <option value="superseded">Superseded</option>}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Reason / notes</label>
          <input type="text" value={form.reason} onChange={set("reason")} className={inputCls} placeholder="Phase 2 quotation revision" data-testid="revision-form-reason" />
        </div>
        <div>
          <label className={labelCls}>Quotation / reference ID (optional)</label>
          <input type="text" value={form.reference} onChange={set("reference")} className={inputCls} data-testid="revision-form-reference" />
        </div>
      </div>
      <p className="text-[10px] text-[#7A6B5F]">Only the latest Confirmed revision drives the displayed current value and balance. Revisions never trigger milestone or payment automation.</p>
      {err && <p className="text-[11px] text-[#A3141A]">{err}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} data-testid="revision-form-submit"
          className="px-4 py-2 bg-[#6E1F2C] text-white text-[10px] tracking-[0.22em] uppercase disabled:opacity-60">
          {busy ? "Saving…" : initial ? "Save revision" : "Add revision"}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-[#6E1F2C]/25 text-[10px] tracking-[0.22em] uppercase text-[#7A6B5F]">Cancel</button>
      </div>
    </form>
  );
}

function ReceiptRow({ r, categories, canEdit, onEdit, onVoid }) {
  const voided = r.status === "void";
  return (
    <div className={`bg-white border border-[#6E1F2C]/12 p-3 md:grid md:grid-cols-12 md:items-center md:gap-3 space-y-1.5 md:space-y-0 ${voided ? "opacity-60" : ""}`}
      data-testid={`ledger-receipt-row-${r.id}`}>
      <div className="md:col-span-2 text-xs text-[#3E2A22]">{r.received_date}</div>
      <div className={`md:col-span-2 text-sm font-semibold ${voided ? "line-through text-[#7A6B5F]" : "text-[#1C110C]"}`} data-testid={`ledger-receipt-amount-${r.id}`}>
        {fmtAEDFils(r.amount_fils)}
      </div>
      <div className="md:col-span-2">
        <span className={`inline-block px-2 py-0.5 text-[9px] tracking-[0.14em] uppercase ${CATEGORY_STYLE[r.category] || CATEGORY_STYLE.other}`}>
          {(categories || {})[r.category] || r.category}
        </span>
      </div>
      <div className="md:col-span-3 text-[11px] text-[#3E2A22]">
        {r.description || "—"}
        {r.milestone_ref && (
          <span className="block text-[10px] text-[#A6822F] mt-0.5"><Link2 size={9} className="inline mr-1" />Ref: {r.milestone_ref.label} (reference only)</span>
        )}
      </div>
      <div className="md:col-span-2 text-[10px] text-[#7A6B5F]">
        {r.created_by_name || "—"} · {(r.created_at || "").slice(0, 10)}
        <span className={`block mt-0.5 font-medium ${voided ? "text-[#A3141A]" : "text-[#3F6E2C]"}`} data-testid={`ledger-receipt-status-${r.id}`}>
          {voided ? `VOID — ${r.void_reason || ""}` : "Active"}
        </span>
      </div>
      {canEdit && !voided && (
        <div className="md:col-span-1 flex gap-2 md:justify-end">
          <button onClick={() => onEdit(r)} title="Edit" className="text-[#6E1F2C]" data-testid={`ledger-receipt-edit-${r.id}`}><Edit3 size={13} /></button>
          <button onClick={() => onVoid(r)} title="Void" className="text-[#A3141A]" data-testid={`ledger-receipt-void-${r.id}`}><Ban size={13} /></button>
        </div>
      )}
    </div>
  );
}

export default function FinanceLedgerSection({ slug }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showRevForm, setShowRevForm] = useState(false);
  const [editingRev, setEditingRev] = useState(null);
  const [busySeed, setBusySeed] = useState(false);

  const load = useCallback(() => {
    portalApi.getFinanceLedger(slug).then(setData).catch((e) => setError(e.message || "Unavailable"));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (error) return null;
  if (!data) return <p className="text-xs text-[#7A6B5F] mb-6">Loading financial ledger…</p>;

  const s = data.snapshot || {};
  const canEdit = !!data.viewer_can_edit;
  const pendingValue = s.current_value_fils === null || s.current_value_fils === undefined;

  const voidReceipt = async (r) => {
    const reason = window.prompt(`Void receipt of ${fmtAEDFils(r.amount_fils)}?\nA reason is required (this is audit-logged; the entry stays in history and is excluded from totals):`);
    if (reason === null) return;
    if (!reason.trim()) { window.alert("A void reason is required."); return; }
    try { await portalApi.voidFinanceReceipt(slug, r.id, { reason: reason.trim() }); load(); }
    catch (e) { window.alert(e.message || "Void failed"); }
  };

  const seed = async () => {
    if (!window.confirm("Seed the four verified 2026 receipts (total AED 300,000) into this ledger?\n\n• Completely separate from the milestone payment schedule\n• No payment status, trigger or reminder is affected\n• No client email is sent\n• Idempotent — already-seeded receipts are skipped")) return;
    setBusySeed(true);
    try { await portalApi.seedPalmReceipts(); load(); }
    catch (e) { window.alert(e.message || "Seed failed"); }
    setBusySeed(false);
  };

  return (
    <div className="mb-8" data-testid="finance-ledger-section">
      <div className="flex items-center flex-wrap gap-3 mb-4">
        <h3 className="text-[11px] tracking-[0.3em] uppercase text-[#6E1F2C] font-medium inline-flex items-center gap-2">
          <Wallet size={14} /> Financial Snapshot & Received-Money Ledger
        </h3>
        {canEdit && data.seed_available && !data.seed_applied && (
          <button onClick={seed} disabled={busySeed} data-testid="ledger-seed-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border border-[#6E1F2C]/40 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-white disabled:opacity-60">
            <ShieldCheck size={11} /> {busySeed ? "Seeding…" : "Seed verified receipts (AED 300,000)"}
          </button>
        )}
        {canEdit && data.seed_applied && (
          <span className="text-[9px] tracking-[0.18em] uppercase text-emerald-700" data-testid="ledger-seed-applied">✓ Verified receipts seeded</span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <SnapshotCard testid="ledger-card-current-value" label="Current Project Value"
          value={pendingValue ? "—" : fmtAEDFils(s.current_value_fils)}
          sub={pendingValue ? "Pending revised quotation" : `Confirmed revision · effective ${s.current_value_effective_date || ""}`} />
        <SnapshotCard testid="ledger-card-total-received" label="Total Amount Received"
          value={fmtAEDFils(s.total_received_fils)}
          sub={`${s.active_receipt_count || 0} active receipt${(s.active_receipt_count || 0) === 1 ? "" : "s"}${s.void_receipt_count ? ` · ${s.void_receipt_count} void (excluded)` : ""}`} />
        <SnapshotCard testid="ledger-card-balance" label="Balance Against Current Value"
          value={pendingValue ? "—" : fmtAEDFils(s.balance_fils)}
          sub={pendingValue ? "Pending revised quotation" : "Current value minus total received"} />
        <SnapshotCard testid="ledger-card-unlinked" label="Outside-Schedule Receipts"
          value={fmtAEDFils(s.outside_schedule_fils)}
          sub="Material advances & receipts outside the milestone payment plan" />
      </div>

      <div className="mb-4"><ReferenceOnlyNotice /></div>

      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] tracking-[0.24em] uppercase text-[#7A6B5F]">Amount Received Ledger</p>
        {canEdit && !showAdd && !editing && (
          <button onClick={() => setShowAdd(true)} data-testid="ledger-add-receipt-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase bg-[#6E1F2C] text-white">
            <Plus size={11} /> Add receipt
          </button>
        )}
      </div>
      {(showAdd || editing) && (
        <div className="mb-3">
          <ReceiptForm slug={slug} categories={data.categories} milestoneOptions={data.milestone_link_options || []}
            initial={editing} onCancel={() => { setShowAdd(false); setEditing(null); }}
            onDone={() => { setShowAdd(false); setEditing(null); load(); }} />
        </div>
      )}
      <div className="hidden md:grid md:grid-cols-12 gap-3 px-3 pb-1 text-[9px] tracking-[0.18em] uppercase text-[#7A6B5F]">
        <span className="col-span-2">Date</span><span className="col-span-2">Amount</span>
        <span className="col-span-2">Category</span><span className="col-span-3">Description</span>
        <span className="col-span-2">Recorded / Status</span><span className="col-span-1" />
      </div>
      <div className="space-y-1.5" data-testid="ledger-receipts-list">
        {(data.receipts || []).length === 0 && (
          <p className="text-xs text-[#7A6B5F] bg-white border border-[#6E1F2C]/10 p-4">No receipts recorded yet.</p>
        )}
        {(data.receipts || []).map((r) => (
          <ReceiptRow key={r.id} r={r} categories={data.categories} canEdit={canEdit}
            onEdit={(x) => { setEditing(x); setShowAdd(false); }} onVoid={voidReceipt} />
        ))}
      </div>

      <div className="flex items-center justify-between mt-6 mb-2">
        <p className="text-[10px] tracking-[0.24em] uppercase text-[#7A6B5F] inline-flex items-center gap-1.5">
          <FileClock size={11} /> Project Value Revision History
        </p>
        {canEdit && !showRevForm && !editingRev && (
          <button onClick={() => setShowRevForm(true)} data-testid="ledger-add-revision-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border border-[#6E1F2C]/40 text-[#6E1F2C]">
            <Plus size={11} /> Add revision
          </button>
        )}
      </div>
      {(showRevForm || editingRev) && (
        <div className="mb-3">
          <RevisionForm slug={slug} initial={editingRev}
            onCancel={() => { setShowRevForm(false); setEditingRev(null); }}
            onDone={() => { setShowRevForm(false); setEditingRev(null); load(); }} />
        </div>
      )}
      <div className="space-y-1.5" data-testid="ledger-revisions-list">
        {(data.revisions || []).length === 0 && (
          <p className="text-xs text-[#7A6B5F] bg-white border border-[#6E1F2C]/10 p-4" data-testid="ledger-revisions-empty">
            Pending revised quotation — no confirmed project value yet.
          </p>
        )}
        {(data.revisions || []).map((rv) => (
          <div key={rv.id} className="bg-white border border-[#6E1F2C]/12 p-3 flex flex-wrap items-center gap-3" data-testid={`ledger-revision-row-${rv.id}`}>
            <span className={`px-2 py-0.5 text-[9px] tracking-[0.14em] uppercase ${REV_STATUS_STYLE[rv.status] || REV_STATUS_STYLE.draft}`}>{rv.status}</span>
            <span className="text-sm font-semibold text-[#1C110C]">{fmtAEDFils(rv.revised_value_fils)}</span>
            <span className="text-[11px] text-[#7A6B5F]">effective {rv.effective_date}</span>
            {rv.reference && <span className="text-[10px] text-[#A6822F]">Ref: {rv.reference}</span>}
            {rv.reason && <span className="text-[11px] text-[#3E2A22]">{rv.reason}</span>}
            {canEdit && (
              <button onClick={() => { setEditingRev(rv); setShowRevForm(false); }} className="ml-auto text-[#6E1F2C]" data-testid={`ledger-revision-edit-${rv.id}`}>
                <Edit3 size={13} />
              </button>
            )}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-[#7A6B5F] mt-3">
        All figures use Asia/Dubai dates and AED. Voided entries remain in history but are excluded from all totals. {data.reference_note}
      </p>
    </div>
  );
}
